import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CommonAxiosGet } from "./CommonUtils/CommonAxios";
import { useNavigate } from "react-router-dom";

const themeTokens = {
    orange: { iconBg: "rgba(231, 124, 43, 0.11);", iconColor: "#e77c2b" },
    green: { iconBg: "rgba(72,177,126,0.11)", iconColor: "#48b17e" },
    teal: { iconBg: "rgba(231, 124, 43, 0.11)", iconColor: "#2d5853" },
    maroon: { iconBg: "rgba(72,177,126,0.11)", iconColor: "#692520" },
};

/* ───────── CARD ───────── */
function ServiceCard({ service, onClick, theme }) {
    const t = themeTokens[theme] || themeTokens.orange;

    return (
        <button className="service-card" onClick={() => onClick(service)} >
            <div
                className="service-icon"
                style={{ background: t.iconBg, color: t.iconColor }}
            >
                🔔
            </div>

            <div className="service-label">{service.service_name}</div>

            <div className="service-open">Open →</div>
        </button>
    );
}

/* ───────── SECTION ───────── */
function SectionBlock({ title, items, onClick, delay }) {

    return (
        <div className="section-block" style={{ animationDelay: `${delay}s` }}>
            <div className="section-header">
                <span className="section-title">{title}</span>
                <div className="section-line" />
                <span className="section-count">{items.length}</span>
            </div>

            <div className="card-grid">
                {items.map((srv, i) => (
                    <ServiceCard
                        key={i}
                        service={srv}
                        onClick={onClick}
                        theme="teal"

                    />
                ))}
            </div>
        </div>
    );
}

/* ───────── MAIN ───────── */
export default function TabsSideBarV2_NewUI() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const modulesData = useSelector(
        (state) => state.reducers?.servicedashboardreducer?.modulesSideBarData
    );

    const RoleId = useSelector(
        (state) => state.reducers?.commonmastersreducer?.dualroles
    );

    const conditions = useSelector(
        (state) => state.reducers?.servicedashboardreducer
    );

    const [servicesData, setServicesData] = useState([]);
    const [activeTab, setActiveTab] = useState("services");

    /* ───────── FETCH ───────── */
    const fetchModulesDetails = async () => {
        if (RoleId > 0 && modulesData?.submodule_id) {
            const res = await CommonAxiosGet(
                `sidebar/submodulesdata/${modulesData.submodule_id}/${RoleId}`
            );

            if (res?.data?.scode === "01") {
                setServicesData(res.data.data);
            }
        }
    };

    useEffect(() => {
        fetchModulesDetails();
    }, [modulesData?.submodule_id, RoleId]);

    const groupedData = useMemo(() => {
        const grouped = {};

        servicesData.forEach((item) => {
            const tab =
                item.innersubmodule_name === "Reports" ? "reports" : "services";

            if (!grouped[tab]) grouped[tab] = {};
            if (!grouped[tab][item.childinnersubmodule_name]) {
                grouped[tab][item.childinnersubmodule_name] = [];
            }

            grouped[tab][item.childinnersubmodule_name].push(item);
        });

        return grouped;
    }, [servicesData]);

    /* ───────── NAVIGATION ───────── */
    const handleNavigate = (service) => {

        // store current sidebar info
        dispatch({
            type: "MODULES_SIDE_BAR_DATA",
            payload: modulesData
        });

        // store selected service
        dispatch({
            type: "CHILDRENMODULEID",
            payload: service.cism_service_id
        });

        let url = service.service_url;

        // dynamic navigation
        if (url === "getUrl()") {

            return conditions.condtionReportVal === 0
                ? navigate("/DrillDownReport")
                : navigate("/SecratraitForm");
        }

        navigate(url);
    };

    const sections = groupedData[activeTab] || {};



    const isAllowedTime = () => {
        const now = new Date();

        //  Cutoff date (2 April 2026)
        const cutoffDate = new Date(2026, 3, 2); // Month is 0-based (3 = April)

        //  If today is AFTER 2 April 2026 → always allow
        if (now > cutoffDate) {
            return true;
        }

        //  Otherwise apply time restriction
        const start = new Date();
        start.setHours(11, 0, 0); // 11:00 AM

        const end = new Date();
        end.setHours(19, 0, 0); // 7:00 PM

        // NOT allowed between 11–7
        //  Allowed outside this time
        return !(now >= start && now <= end);
    };

    return (
        <div className="dashboard-root">
            <div className="dashboard-main">

                {/* Breadcrumb */}
                <div className="breadcrumb">
                    <span>{modulesData?.module_name}</span>
                    <span>/</span>
                    <span className="breadcrumb-active">
                        {modulesData?.submodule_name}
                    </span>
                </div>

                {/* Title */}
                <div style={{ marginBottom: 20 }}>
                    <div className="page-title">{modulesData?.submodule_name}</div>
                    <div className="title-underline" />
                </div>

                {/* Tabs */}
                <div className="tabs-container">
                    {["services", "reports"].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                        >
                            {tab === "services" ? "📋 Services" : "📊 Reports"}
                        </button>
                    ))}
                </div>

                {/* Sections */}
                {Object.entries(sections).map(([title, items], i) => (
                    <SectionBlock
                        key={title}
                        title={title}
                        items={items.filter(item =>
                            item.service_name?.trim().toLowerCase() !== "contingent bill" ||
                            isAllowedTime()
                        )}
                        onClick={handleNavigate}
                        delay={i * 0.05}
                    />
                ))}
            </div>
        </div>
    );
}